import { unwrapResult } from "@reduxjs/toolkit";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import useGtmQuestionSet from "@/app/common-hooks/gtm/use-gtm-question-set";
import { QuotePayload } from "@/app/common-types/quote/quote-data";
import { TripDetails } from "@/app/common-types/quote/trip-details";
import { useEditFlowType } from "@/app/common-types/use-edit-quote-review-summary-flow-type";
import selectLocationHookFlash from "@/app/common-utils/common.utils";
import {
  clearEditFlowSession,
  getQuoteReviewSummaryEditSection,
} from "@/app/common-utils/edit-quote-review-summary-flow.utils";
import { Routes } from "@/app/config/page-view-constants";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import {
  QuestionFour,
  RenewalPage,
  CruiseEdit,
  QUOTE_JOURNEY_TYPE,
} from "@/config/app-constant";
import { APPLICATION, BRAND_AVANTI, ESSENTIALS, PRODUCT } from "@/constants";
import {
  saveQuestionDataBFF,
  sessionCheck,
  updateQuoteOnQuestion,
} from "@/store/slices/quote/quote.action";
import { setQuestionData } from "@/store/slices/quote/quote.slice";
import { RootState, store } from "@/store/store";

import { useCruiseContext } from "../provider/cruise.provider";

function useCruiseHooks() {
  const dispatch = useDispatch<typeof store.dispatch>();
  const quoteResponse = useSelector((state: RootState) => state.quote);
  const [isFooterLoading, setIsFooterLoading] = useState(false);
  const { loading, attributes } = quoteResponse;
  const { tripDetails } = attributes;
  const policyPackage = tripDetails?.policyPackage;

  const router = useRouter();
  const searchParams = useSearchParams();
  const editPage = searchParams.get(CruiseEdit);
  const { current } = useWizard();
  const { isLastStep: isLastStepInCoverEditQuoteSummary } =
    useEditFlowType(editPage);
  const {
    selectedAnswer,
    setSelectedAnswer,
    setIsOpenUnsavedChangesModal,
    isCruiseModalOpen,
    setIsCruiseModalOpen,
  } = useCruiseContext();
  const [payload, setPayload] = useState<QuotePayload | null>(null);
  const answerMap: Record<number, boolean | undefined> = {
    1: true,
    2: false,
  };

  useEffect(() => {
    const goingOnCruise = quoteResponse?.attributes?.tripDetails?.goingOnCruise;
    if (goingOnCruise === true) {
      setSelectedAnswer(1);
    } else if (goingOnCruise === false) {
      setSelectedAnswer(2);
    } else {
      setSelectedAnswer(0);
    }
  }, [quoteResponse, setSelectedAnswer]);

  const handleSubmit = async () => {
    if (selectedAnswer === 0) return;
    try {
      setIsFooterLoading(true);
      const goingOnCruise = answerMap[selectedAnswer];
      if (
        APPLICATION === RenewalPage &&
        PRODUCT === "avanti" &&
        isCruiseModalOpen === false &&
        policyPackage === ESSENTIALS &&
        goingOnCruise !==
          quoteResponse?.attributes?.tripDetails?.goingOnCruise &&
        quoteResponse?.attributes?.tripDetails?.goingOnCruise === false
      ) {
        setIsFooterLoading(false);
        setIsCruiseModalOpen(true);
        return;
      }

      const payloadData: QuotePayload = {
        attributes: {
          ...quoteResponse.attributes,
          tripDetails: {
            ...quoteResponse.attributes.tripDetails,
            goingOnCruise,
          },
        },
      };
      setPayload(payloadData);
      dispatch(setQuestionData(payloadData));

      if (!quoteResponse?.referenceId) {
        const newPayload: QuotePayload = {
          type: "IntermediateData",
          channel: "ONLINE",
          attributes: {
            ...payloadData.attributes,
          },
        };

        const questionData = dispatch(
          saveQuestionDataBFF({
            payload: { ...newPayload, viewPage: Routes.Cruise },
            dispatch,
            router,
          })
        );
        const originalPromiseResult = unwrapResult(await questionData);
        if (originalPromiseResult) {
          router.push(current.next ?? "");
        }
      } else if (editPage) {
        const result = await dispatch(
          updateQuoteOnQuestion({
            payload: {
              ...payloadData,
              viewPage: Routes.Cruise,
              referenceId: quoteResponse.referenceId,
              type: QUOTE_JOURNEY_TYPE,
              channel: "ONLINE",
            },
            router,
            dispatch,
          })
        );
        const originalPromiseResult = unwrapResult(result);
        if (originalPromiseResult) {
          if (isCruiseModalOpen) {
            router.push(current.edges.cruiseCoverModal ?? "");
            return;
          }
          let nextPage = current?.edges?.editFlowNext;

          // Check if user came from quote-review (sessionStorage is set)
          const editSection = getQuoteReviewSummaryEditSection();
          if (
            PRODUCT === BRAND_AVANTI &&
            goingOnCruise &&
            editSection === "cover"
          ) {
            clearEditFlowSession();
            router.push("/quote");
            return;
          }

          if (editSection === "cover") {
            // User came from quote-review, use new flow
            nextPage = current?.edges?.coverEditQuoteSummaryNext; // → /cruise/?coverEdit=true
          } else {
            // User came from quote page, use old flow
            nextPage = current?.edges?.editFlowNext; // → /travel-dates/?coverEdit=true
          }

          router.push(nextPage ?? "");
        }
      } else {
        dispatch(sessionCheck({ dispatch, router }));
        let path = current.next ?? "";
        if (editPage) {
          path = current.edges.editFlowNext ?? "";
        }
        router.push(path);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error in handleSubmit:", error);
      setIsFooterLoading(false);
    }
  };

  // GTM

  const toLocationData = selectLocationHookFlash(
    payload?.attributes?.tripDetails as TripDetails
  );

  useGtmQuestionSet({
    pageTitle: QuestionFour,
    tripType: payload?.attributes?.tripDetails?.policyType ?? undefined,
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    destination: toLocationData.destination,
    destinationAdditions: toLocationData.destinationAdditions,
    cruise: payload?.attributes?.tripDetails?.goingOnCruise ?? undefined,
    tripExtra: payload?.attributes?.tripDetails?.goingOnCruise
      ? QuestionFour
      : undefined,
    travelFrom: payload?.attributes?.tripDetails?.fromLocation ?? undefined,
    isSubmitBtnClicked: payload !== null,
  });

  const handleBackButton = () => {
    if (APPLICATION === RenewalPage) {
      if (editPage) {
        setIsOpenUnsavedChangesModal(true);
      } else {
        router.push(current.prev ?? "");
      }
    } else {
      router.push(current.prev ?? "");
    }
  };

  const handleCruiseCoverModalConfirmButton = () => {
    if (APPLICATION === RenewalPage && PRODUCT === "avanti") {
      setIsCruiseModalOpen(false);
      setIsFooterLoading(true);
      handleSubmit();
    }
  };

  const handleCruiseCoverModalBackButton = () => {
    setIsFooterLoading(false);
    setIsCruiseModalOpen(false);
  };

  return {
    isFooterLoading,
    isHideFooterBack: APPLICATION !== RenewalPage && !!editPage,
    loading,
    setIsFooterLoading,
    handleSubmit,
    handleBackButton,
    handleCruiseCoverModalConfirmButton,
    handleCruiseCoverModalBackButton,
    isLastStepInCoverEditQuoteSummary,
  };
}
export default useCruiseHooks;
